import { TWidget } from 'widgets/Widget';

// serviceName: state
type TConsentState = Record<string, boolean>;
export type TCategoryConsentState = Record<'ESSENTIAL' | 'MARKETING' | 'FUNCTIONAL', boolean>;

const SERVICE_CHANGE_EVENT = 'consentServiceChange';

/**
 * This Widget represents a Consent Management in it's disabled state and
 * should also be used as a base class for extending with a specific provider
 * implementation.
 *
 * In order to consume the consent state and updates the widgets should send an event
 * with the name `consentServiceRequest` and the service ID as a parameter, as soon as the
 * state is available this widget will emit an event with the name `consentServiceChange`
 * and the service ID and the acceptance state as parameters.
 *
 * In order to implement a specific consent provider, you should extend this class
 * and implement the `initializeConsentProvider` without calling the super method.
 * Also you should implement the `openSettings` method to open the consent management window.
 * The subclass should also call the `updateConsentState` or `updateCategoryConsentState`
 * methods to update the consent state based on the user actions.
 *
 * See the UsercentricsCM widget for an example of how to implement a specific provider.
 *
 * @param Widget Base widget for extending
 * @returns ConsentManagement class
 */
function ConsentManagementClassCreator(Widget: TWidget) {
    class ConsentManagement extends Widget {
        private consentState: TConsentState | null = null;

        public consentManagementCategories = ['ESSENTIAL', 'MARKETING', 'FUNCTIONAL'] as const;

        prefs() {
            return {
                settings: {
                    serviceMap: <Record<'ESSENTIAL|MARKETING|FUNCTIONAL', Array<string>>>{}
                },
                ...super.prefs()
            };
        }

        init() {
            super.init();

            this.eventBus().on('consentSettingsClicked', 'openSettings');
            this.eventBus().on('consentServiceRequest', 'onServiceRequest');
            this.initializeConsentProvider();
        }

        /**
         * This method should be implemented in the extending class to initialize
         * the specific consent provider.
         * The default implementation should only be called if the consent management
         * is disabled thus all services and categories are accepted by default.
         */
        initializeConsentProvider() {
            const serviceList = Object.values(this.prefs().settings.serviceMap).flat();

            this.updateConsentState(Object.fromEntries(serviceList.map(serviceId => [serviceId, true])));
        }

        /**
         * This method should be implemented in the extending class to open the
         * consent management settings dialog.
         * The default implementation throws an error to indicate that it should
         * be overridden.
         */
        openSettings() {
            throw new Error('Method "openSettings" should be implemented in the extending class');
        }

        /**
         * This method should be called from the extending class to update the consent state.
         * It will emit events for each service that has changed its acceptance state.
         * Or reload the page if the acceptance state was changed to false.
         *
         * If your provider only supports categories, you should call `updateCategoryConsentState`
         * instead, which will convert the category state to service state and call this method.
         *
         * @param state The new consent state to update.
         */
        protected updateConsentState(state: TConsentState) {
            if (!this.consentState) {
                // Initial state, save and announce it
                Object.entries(state).forEach(([serviceId, accepted]) => {
                    this.eventBus().emit(SERVICE_CHANGE_EVENT, serviceId, accepted);
                });
            } else {
                // Update state, check for changes and emit events accordingly
                Object.entries(this.consentState).forEach(([serviceId, wasAccepted]) => {
                    if (state[serviceId] !== wasAccepted) {
                        if (!wasAccepted) {
                            this.eventBus().emit(SERVICE_CHANGE_EVENT, serviceId, true);
                        } else {
                            window.location.reload();
                        }
                    }
                });
            }

            this.consentState = state;
        }

        /**
         * This method updates the consent state based on the provided category consent state.
         * It converts the category state to service state and calls `updateConsentState`.
         * If your provider supports both categories and services, don't use this method directly
         * use `updateConsentState` instead.
         *
         * @param state The category consent state to update.
         */
        protected updateCategoryConsentState(state: TCategoryConsentState) {
            const serviceState = Object.fromEntries(
                Object.entries(state).reduce((acc: Array<[string, boolean]>, [category, isConsentGiven]: [string, boolean]) => {
                    const categoryServices: Array<string> = this.prefs().settings.serviceMap[category] ?? [];
                    const states: Array<[string, boolean]> = categoryServices.map(serviceId => [serviceId, isConsentGiven]);

                    return [...acc, ...states];
                }, [])
            );

            this.updateConsentState(serviceState);
        }

        /**
         * This method handles the service request events from other widgets.
         * It replies with the current consent state for the requested service in a separate event.
         *
         * @param serviceId The ID of the requested service.
         */
        onServiceRequest(serviceId: string) {
            if (!this.consentState) {
                // Likely the provider is not initialized yet, we will send all the services when it will be.
                return;
            }

            const isAccepted = this.consentState[serviceId] || false;

            this.eventBus().emit(SERVICE_CHANGE_EVENT, serviceId, isAccepted);
        }
    }

    return ConsentManagement;
}

export type TConsentManagement = ReturnType<typeof ConsentManagementClassCreator>;

export type TConsentManagementInstance = InstanceType<TConsentManagement>;

export default ConsentManagementClassCreator;
